import { Heart, MapPin, Star, Bed, Bath, Users, Check, ChevronLeft, ChevronRight, X, ShieldCheck } from "lucide-react";
import { memo, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { cn, formatKz, getSafeImageUrls, getFirstImageUrl } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { useUserCurrency } from "@/hooks/useUserCurrency";

import VerifiedBadge, { SpaceTypeBadge, InsuredBadge } from "@/components/VerifiedBadge";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { markImageLoaded } from "@/lib/imageCache";
import { trackPropertyClick } from "@/lib/propertyTracking";

interface PropertyCardProps {
  property: {
    id: string;
    title: string;
    images: string[];
    price_per_night: number;
    currency: string;
    city: string;
    province: string;
    property_type: string;
    bedrooms: number;
    bathrooms: number;
    max_guests: number;
    average_rating: number;
    total_reviews: number;
    is_verified?: boolean;
    has_insurance?: boolean;
    discount_percent?: number | null;
  };
  compact?: boolean;
  priority?: boolean; // For eager loading first images
}

/**
 * PropertyCard Otimizado
 * 
 * Performance improvements:
 * - Memoizado com React.memo para evitar re-renders
 * - useCallback para handlers estáveis
 * - OptimizedImage com lazy loading e blur placeholder
 * - Prioridade configurável para imagens acima da dobra
 */
const PropertyCard = memo(({ property, compact = false, priority = false }: PropertyCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useUserCurrency();
  const fmt = (v: number) => (currency === "AOA" ? formatKz(v) : formatMoney(v, currency));

  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const lastTapRef = useRef<number>(0);
  const navigateTimeoutRef = useRef<number | null>(null);

  const images = getSafeImageUrls((property as any).images);
  const safeImages = images.length > 0 ? images : [getFirstImageUrl(null)];
  const safeIndex = Math.min(currentImageIndex, safeImages.length - 1);

  // Pré-carrega todas as imagens do carrossel para eliminar o flash ao trocar de dot
  useEffect(() => {
    safeImages.forEach((url) => {
      if (!url) return;
      const img = new Image();
      img.onload = () => markImageLoaded(url);
      img.src = url;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeImages.join('|')]);

  const discountPercent = property.discount_percent !== undefined && property.discount_percent !== null && property.discount_percent > 0
    ? property.discount_percent
    : (property.id === "521a7f18-2be4-4510-a2af-e8a0a896b8c6" ? 10 : 0);
  const hasDiscount = discountPercent > 0;
  const discountedPrice = hasDiscount
    ? property.price_per_night * (1 - discountPercent / 100)
    : property.price_per_night;

  // Handlers memoizados para evitar recriação a cada render
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    trackPropertyClick(property.id, "card");
    navigate(`/imovel/${property.id}`);
  }, [navigate, property.id]);

  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(prev => !prev);
  }, []);

  const handleImageDotClick = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(idx);
  }, []);

  const handleImageTap = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    const delta = now - lastTapRef.current;
    lastTapRef.current = now;

    if (navigateTimeoutRef.current) {
      window.clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = null;
    }

    if (delta > 0 && delta < 320) {
      setShowLightbox(true);
      return;
    }

    navigateTimeoutRef.current = window.setTimeout(() => {
      trackPropertyClick(property.id, "card");
      navigate(`/imovel/${property.id}`);
    }, 320);
  }, [navigate, property.id]);

  useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current) {
        window.clearTimeout(navigateTimeoutRef.current);
        navigateTimeoutRef.current = null;
      }
    };
  }, []);

  const nextLightbox = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % safeImages.length);
  }, [safeImages.length]);

  const prevLightbox = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + safeImages.length) % safeImages.length);
  }, [safeImages.length]);

  // Compact version for mobile — Airbnb-style
  if (compact) {
    return (
      <>
        {showLightbox && (
          <div className="fixed inset-0 z-50 bg-black">
            <div className="absolute top-4 left-4 z-10 text-white">
              <span className="text-base font-medium">
                {safeIndex + 1} / {safeImages.length}
              </span>
            </div>
            <button
              onClick={() => setShowLightbox(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {safeImages.length > 1 && (
              <>
                <button
                  onClick={prevLightbox}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 z-10"
                >
                  <ChevronLeft className="w-7 h-7 text-white" />
                </button>
                <button
                  onClick={nextLightbox}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 z-10"
                >
                  <ChevronRight className="w-7 h-7 text-white" />
                </button>
              </>
            )}

            <img
              src={safeImages[safeIndex]}
              alt={`${property.title} - ${safeIndex + 1}`}
              className="w-full h-full object-contain"
              loading="eager"
              decoding="async"
              onError={(e) => {
                (e.target as HTMLImageElement).src = getFirstImageUrl(null);
              }}
            />

            {safeImages.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-1.5">
                {safeImages.slice(0, 10).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all",
                      idx === safeIndex ? "bg-white w-4" : "bg-white/50"
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div 
          onClick={handleCardClick}
          className="group relative bg-white dark:bg-[#1C1C1E] rounded-[24px] overflow-hidden shadow-sm dark:shadow-none border border-gray-100 dark:border-[#2A2A2A] active:scale-[0.98] transition-transform cursor-pointer flex flex-col"
        >
          {/* Image — top rounded */}
          <div className="relative aspect-[4/3] w-full overflow-hidden" onClick={handleImageTap}>
            <OptimizedImage
              src={safeImages[safeIndex]}
              alt={property.title}
              priority={true}
              aspectRatio="4/3"
              objectFit="cover"
              className="w-full h-full object-cover"
            />
          
          {/* Favorite Button */}
          <button
            onClick={handleFavoriteClick}
            className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-full bg-white/80 dark:bg-black/40 backdrop-blur-md shadow-sm dark:shadow-none"
          >
            <Heart
              className={cn(
                "w-4 h-4 transition-colors",
                isFavorite ? "fill-[#E8513D] text-[#E8513D]" : "text-zinc-600 dark:text-white"
              )}
            />
          </button>

          {/* Verified + Insurance Badges */}
          <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1 items-start">
            {property.is_verified && <VerifiedBadge size="xs" />}
            {(property as any).insurance_badge_active && <InsuredBadge size="xs" />}
          </div>

          {/* Image Dots */}
          {safeImages.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {safeImages.slice(0, 5).map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => handleImageDotClick(e, idx)}
                  className={cn(
                    "w-[6px] h-[6px] rounded-full transition-all",
                    idx === safeIndex ? "bg-white scale-110" : "bg-white/50"
                  )}
                />
              ))}
            </div>
          )}
          </div>

        {/* Content */}
        <div className="p-3">
          {/* Location & Rating */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1 text-zinc-500 dark:text-zinc-400">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="text-[11px] font-medium truncate max-w-[100px]">{property.city}, {property.province}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Star className="w-3.5 h-3.5 fill-zinc-900 text-zinc-900 dark:fill-zinc-100 dark:text-zinc-100" />
              <span className="text-[12px] font-bold text-zinc-700 dark:text-zinc-300">
                {property.average_rating > 0 ? property.average_rating.toFixed(1) : "Novo"}
              </span>
            </div>
          </div>

          {/* Title */}
          <h3 className="font-display font-bold text-[14px] text-zinc-900 dark:text-zinc-100 leading-tight mb-2 line-clamp-1">
            {property.title}
          </h3>

          {/* Amenities */}
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 dark:text-zinc-400 font-medium mb-2.5 flex-wrap">
            <div className="flex items-center gap-1 shrink-0">
              <Bed className="w-3 h-3" />
              <span>{property.bedrooms} {t("explore.bedrooms") || "quartos"}</span>
            </div>
            <span className="text-zinc-300 dark:text-zinc-600">·</span>
            <div className="flex items-center gap-1 shrink-0">
              <Bath className="w-3 h-3" />
              <span>{property.bathrooms} {t("explore.bathrooms") || "WC"}</span>
            </div>
            <span className="text-zinc-300 dark:text-zinc-600">·</span>
            <div className="flex items-center gap-1 shrink-0">
              <Users className="w-3 h-3" />
              <span>{property.max_guests} {t("property.guests") || "hósp."}</span>
            </div>
          </div>

          {/* Price */}
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-baseline gap-1">
              {hasDiscount ? (
                <>
                  <span className="text-[15px] font-black text-zinc-900 dark:text-zinc-100 leading-none">
                    {fmt(discountedPrice)}
                  </span>
                  <span className="text-[11px] text-zinc-400 line-through">
                    {fmt(property.price_per_night)}
                  </span>
                </>
              ) : (
                <span className="text-[15px] font-black text-zinc-900 dark:text-zinc-100 leading-none">
                  {fmt(property.price_per_night)}
                </span>
              )}
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium pb-0.5">/{t("property.night") || "noite"}</span>
            </div>
            {hasDiscount && (
              <span className="text-[9px] font-bold text-[#E8513D] bg-[#FFF0ED] dark:bg-[#E8513D]/10 px-1.5 py-0.5 rounded">
                {discountPercent}% de desconto
              </span>
            )}
          </div>
        </div>
      </div>
      </>
    );
  }

  // Full version for desktop
  return (
    <div 
      onClick={handleCardClick}
      className="group relative bg-card rounded-2xl overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    >
      {/* Image Carousel */}
      <div className="relative aspect-[4/3]">
        <div className="absolute inset-0 overflow-hidden rounded-t-2xl">
          <OptimizedImage
            src={safeImages[safeIndex]}
            alt={property.title}
            className="group-hover:scale-105"
            priority={true}
            aspectRatio="4/3"
            objectFit="cover"
          />
        </div>
        
        {/* Image Dots */}
        {safeImages.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {safeImages.slice(0, 5).map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => handleImageDotClick(e, idx)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  idx === safeIndex
                    ? "bg-white scale-110"
                    : "bg-white/60 hover:bg-white/80"
                )}
              />
            ))}
          </div>
        )}

        {/* Favorite Button */}
        <button
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors shadow-lg"
        >
          <Heart
            className={cn(
              "w-5 h-5 transition-colors",
              isFavorite ? "fill-red-500 text-red-500" : "text-white"
            )}
          />
        </button>

        {/* Property Type & Verified & Insurance Badges */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2 flex-wrap">
          <SpaceTypeBadge type={property.property_type} size="sm" />
          {property.is_verified && <VerifiedBadge size="sm" />}
          {(property as any).insurance_badge_active && <InsuredBadge size="sm" />}
        </div>
      </div>

      {/* Content */}
      <div className="p-3.5 space-y-2">
        {/* Location & Rating */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-muted-foreground text-xs max-w-[70%]">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{property.city}, {property.province}</span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <Star className="w-3.5 h-3.5 fill-zinc-900 text-zinc-900 dark:fill-zinc-100 dark:text-zinc-100" />
            <span className="font-medium text-xs">
              {property.average_rating > 0 ? property.average_rating.toFixed(1) : "Novo"}
            </span>
            {property.average_rating > 0 && (
              <span className="text-muted-foreground text-xs">({property.total_reviews})</span>
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="font-display font-semibold text-base leading-tight line-clamp-2 group-hover:text-primary transition-colors">
          {property.title}
        </h3>

        {/* Amenities */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Bed className="w-3.5 h-3.5" />
            <span>{property.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bath className="w-3.5 h-3.5" />
            <span>{property.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span>{property.max_guests}</span>
          </div>
        </div>

        {/* Price */}
        <div className="pt-2 border-t border-border">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            {hasDiscount ? (
              <>
                <span className="text-lg font-black text-zinc-900 dark:text-zinc-100">
                  {fmt(discountedPrice)}
                </span>
                <span className="text-muted-foreground text-xs line-through">
                  {fmt(property.price_per_night)}
                </span>
                <Badge variant="outline" className="bg-[#FFF0ED] text-[#E8513D] dark:bg-[#E8513D]/10 border-[#E8513D]/30 text-[10px] font-semibold py-0.5 px-1.5">
                  -{discountPercent}%
                </Badge>
              </>
            ) : (
              <span className="text-lg font-black text-zinc-900 dark:text-zinc-100">
                {fmt(property.price_per_night)}
              </span>
            )}
            <span className="text-muted-foreground text-xs">{t('search.perNight')}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

PropertyCard.displayName = 'PropertyCard';

export default PropertyCard;
